import axios, { AxiosError, type AxiosInstance } from 'axios';
import { ApiException, CLIENT_ERROR_CODES, type ApiResponse } from './types';

/**
 * API 호출 단일 진입점.
 *
 * 컴포넌트에서 axios나 fetch를 직접 쓰지 않는다. (CLAUDE.md 5.2절)
 * 직접 쓰면 에러 처리와 응답 언랩이 화면마다 제각각이 되고,
 * 타임아웃이 없는 호출이 섞여 들어간다.
 *
 * 인증에 대해 (Phase 0.5, 2026-08-15)
 *   토큰이나 로그인 헤더를 붙이지 않는다. 서버 세션 쿠키를 브라우저가 자동으로 싣는다.
 *   프론트가 자격증명을 들고 있지 않는 것이 서버 세션을 고른 이유 중 하나다 —
 *   localStorage 에 JWT 를 두면 XSS 한 번에 그대로 새어 나간다.
 *
 *   이전 판은 "Cloudflare Access가 앱 도달 이전에 인증을 끝낸다" 였다.
 *   Access는 켜진 적이 없고, 가족이 함께 쓰기로 하면서 앱 자체 로그인으로 바뀌었다.
 *   (00-architecture.md 1절)
 */

const instance: AxiosInstance = axios.create({
  // nginx가 /api 를 백엔드로 프록시한다. 로컬 개발은 vite.config.ts의 proxy가 담당한다.
  baseURL: '/api',
  timeout: 15_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * 응답 인터셉터.
 *
 * 백엔드는 성공/실패 모두 ApiResponse 형태로 내려준다.
 * 여기서 두 가지를 한다.
 *   1. success=false 면 ApiException을 던진다 (호출부가 if 분기를 안 하도록)
 *   2. axios 에러를 ApiException으로 정규화한다
 */
instance.interceptors.response.use(
  (response) => {
    const body = response.data as ApiResponse<unknown>;

    // ApiResponse 형태가 아니면 그대로 통과시킨다.
    // (actuator 같은 백엔드 기본 엔드포인트를 직접 부를 때)
    if (body === null || typeof body !== 'object' || !('success' in body)) {
      return response;
    }

    if (!body.success) {
      const error = body.error;
      throw new ApiException(
        error?.code ?? CLIENT_ERROR_CODES.UNKNOWN,
        error?.message ?? '알 수 없는 오류가 발생했습니다.',
        response.status,
      );
    }

    return response;
  },
  (error: unknown) => {
    const normalized = normalizeError(error);

    if (isSessionExpired(normalized) && unauthenticatedHandler) {
      unauthenticatedHandler();
    }

    throw normalized;
  },
);

/**
 * 세션이 끊겼을 때 부를 콜백.
 *
 * 인터셉터에서 곧바로 화면을 옮기지 않는 이유
 *   여기는 React 바깥이라 useNavigate 를 쓸 수 없다.
 *   window.location 으로 밀어버리면 SPA 가 통째로 다시 로딩되고,
 *   TanStack Query 캐시와 입력 중이던 값이 전부 날아간다.
 *   앱이 시작할 때 이 자리에 라우터 이동 함수를 꽂는다. (App.tsx)
 */
let unauthenticatedHandler: (() => void) | null = null;

export function setUnauthenticatedHandler(handler: (() => void) | null): void {
  unauthenticatedHandler = handler;
}

/**
 * 세션 만료(A004)인지 판정한다.
 *
 * 🔴 401 이라고 전부 세션 만료가 아니다
 *   로그인 실패(A001)도 401 로 온다. 상태 코드만 보고 처리하면
 *   비밀번호를 한 번 틀릴 때마다 로그인 페이지로 튕겨 입력값이 사라진다.
 *   그래서 에러 코드로 가른다.
 */
function isSessionExpired(exception: ApiException): boolean {
  return exception.status === 401 && exception.code === 'A004';
}

/**
 * axios 에러를 ApiException으로 변환한다.
 *
 * 서버가 4xx/5xx를 내려도 본문이 ApiResponse 형태면 백엔드의 code/message를 살린다.
 * GlobalExceptionHandler가 만든 메시지가 화면에 그대로 보이게 하기 위함이다.
 */
function normalizeError(error: unknown): ApiException {
  if (error instanceof ApiException) {
    return error;
  }

  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiResponse<unknown>>;

    if (axiosError.code === 'ECONNABORTED') {
      return new ApiException(
        CLIENT_ERROR_CODES.TIMEOUT,
        '요청 시간이 초과되었습니다. 잠시 후 다시 시도해 주세요.',
        0,
      );
    }

    const response = axiosError.response;
    if (!response) {
      return new ApiException(
        CLIENT_ERROR_CODES.NETWORK,
        '서버에 연결할 수 없습니다. 네트워크 상태를 확인해 주세요.',
        0,
      );
    }

    const body = response.data;
    if (body && typeof body === 'object' && 'error' in body && body.error) {
      return new ApiException(body.error.code, body.error.message, response.status);
    }

    return new ApiException(
      CLIENT_ERROR_CODES.UNKNOWN,
      `서버 오류가 발생했습니다. (${response.status})`,
      response.status,
    );
  }

  return new ApiException(
    CLIENT_ERROR_CODES.UNKNOWN,
    error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.',
    0,
  );
}

/**
 * ApiResponse의 data만 꺼내 반환한다.
 *
 * 호출부가 res.data.data 같은 이중 접근을 하지 않게 한다.
 * success=false인 경우는 인터셉터가 이미 던졌으므로 여기 도달하지 않는다.
 */
function unwrap<T>(body: ApiResponse<T>): T {
  return body.data as T;
}

export const api = {
  async get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    const response = await instance.get<ApiResponse<T>>(url, { params });
    return unwrap(response.data);
  },

  async post<T>(url: string, body?: unknown): Promise<T> {
    const response = await instance.post<ApiResponse<T>>(url, body);
    return unwrap(response.data);
  },

  async put<T>(url: string, body?: unknown): Promise<T> {
    const response = await instance.put<ApiResponse<T>>(url, body);
    return unwrap(response.data);
  },

  async delete<T>(url: string): Promise<T> {
    const response = await instance.delete<ApiResponse<T>>(url);
    return unwrap(response.data);
  },

  /*
    🔴 patch 가 없다. 일부러 안 넣었다 (2026-08-16)

    세션 B 가 가계부 고정비 켜기/끄기를 PATCH 로 만들려다 이 빈칸을 만나
    백엔드를 POST 로 맞췄다. 그 선택 자체는 문제가 없다 — 토글을 POST 로 두는 것은
    흔한 방식이고, 공유 파일을 여는 것보다 싸다는 판단도 맞다.

    그래도 여기 적어두는 이유는, 같은 일이 반복되면
    "POST 만 있는 API" 가 결정이 아니라 사고로 만들어지기 때문이다.
    프론트 클라이언트의 빈칸이 백엔드 설계를 바꾸는 것을 한 번은 넘어가되
    두 번째부터는 알고 넘어가야 한다.

    쓰는 곳 없이 미리 넣지 않는 이유
      호출부도 테스트도 없는 메서드가 들어가면 첫 사용자가 "이게 되나" 부터 확인해야 한다.
      인터셉터와 에러 정규화는 인스턴스에 붙어 있어 자동으로 적용되므로,
      정말 필요해지는 순간 아래 형태로 세 줄만 추가하면 된다.

        async patch<T>(url: string, body?: unknown): Promise<T> {
          const response = await instance.patch<ApiResponse<T>>(url, body);
          return unwrap(response.data);
        },

    2026-08-16 기준 백엔드에 @PatchMapping 은 0건이고, rawClient 나 axios 를
    직접 써서 우회하는 곳도 없다. 지금은 어긋난 데가 없다.
  */

  /**
   * 파일 업로드. 가계부 엑셀과 혜택 바코드 이미지가 쓴다.
   * Content-Type을 지우는 이유: axios가 FormData의 boundary를 포함해 직접 설정해야 한다.
   *
   * fields 는 파일과 함께 보낼 텍스트 값이다 (바코드의 label·memo).
   * 백엔드는 @RequestParam 으로 받는다.
   *
   * 값이 undefined 인 항목은 아예 싣지 않는다.
   *   FormData 에 append 하면 무엇이든 문자열이 되어, undefined 가
   *   "undefined" 라는 다섯 글자로 서버에 도착한다. 메모를 비운 것과
   *   메모에 "undefined" 를 적은 것이 구분되지 않는다.
   */
  async upload<T>(
    url: string,
    file: File,
    fieldName = 'file',
    fields?: Record<string, string | undefined>,
  ): Promise<T> {
    const formData = new FormData();
    formData.append(fieldName, file);

    for (const [key, value] of Object.entries(fields ?? {})) {
      if (value !== undefined) {
        formData.append(key, value);
      }
    }

    const response = await instance.post<ApiResponse<T>>(url, formData, {
      headers: { 'Content-Type': undefined },
      // 엑셀 파싱은 오래 걸릴 수 있어 기본 타임아웃보다 길게 준다.
      timeout: 120_000,
    });
    return unwrap(response.data);
  },

  /**
   * 파일 교체(PUT). 바코드 이미지 갈아 끼우기에 쓴다.
   *
   * upload 와 나눈 이유 - POST 는 "새로 만든다", PUT 은 "그 자리를 바꾼다" 다.
   * 바코드 id 를 유지해야 마지막 사용 시각이 살아남는다.
   */
  async uploadPut<T>(url: string, file: File, fieldName = 'file'): Promise<T> {
    const formData = new FormData();
    formData.append(fieldName, file);

    const response = await instance.put<ApiResponse<T>>(url, formData, {
      headers: { 'Content-Type': undefined },
      timeout: 120_000,
    });
    return unwrap(response.data);
  },
};

/** 인터셉터를 거치지 않는 원본 인스턴스. actuator 등 비-ApiResponse 응답에 쓴다. */
export const rawClient = instance;
